/**
 * Main Navigation Configuration
 * This is the single source of truth for all site navigation (desktop, tablet, mobile)
 *
 * To change navigation:
 * 1. Edit this file's getNavigation() function
 * 2. Navigation automatically updates everywhere (desktop menu, mobile menu, consolidated menu)
 *
 * Dynamic menus (age groups, periods, categories) are loaded from API
 */

import { useAgeGroups } from '~/composables/useAgeGroups'
import { useCampCategories } from '~/composables/useCampCategories'
import { useCamps } from '~/composables/useCamps'
import { useLocations } from '~/composables/useLocations'
import { usePeriods } from '~/composables/usePeriods'
import { logApiError } from '~/utils/logger'

interface MenuItem {
  id: string
  name: string
  city?: string | null
}

interface NavItem {
  title: string
  to?: { name: string, query?: Record<string, string> }
  icon?: { icon: string }
  children?: NavItem[]
}

export async function getNavigation() {
  // Load dynamic menu data from API
  const { getAgeGroups } = useAgeGroups()
  const { getPeriods } = usePeriods()
  const { getCategories } = useCampCategories()
  const { getLocations } = useLocations()
  const { getCamps } = useCamps()

  let ageGroupsMenu: { label: string, value: string }[] = []
  let periodsMenu: { label: string, value: string }[] = []
  let categoriesMenu: { label: string, value: string }[] = []
  let locationsMenu: { label: string, value: string }[] = []

  try {
    const [
      ageGroupsResponse,
      periodsResponse,
      categoriesResponse,
      locationsResponse,
      campsResponse,
    ] = await Promise.all([
      getAgeGroups(true),
      getPeriods(true),
      getCategories({ isActive: true }),
      getLocations({ isActive: true }),
      getCamps({ status: 'published' }),
    ])

    const campAgeGroupIds = new Set<string>()
    const campPeriodIds = new Set<string>()
    const campLocationIds = new Set<string>()

    const campLocationNames = new Set<string>()

    ;(campsResponse?.data || []).forEach(camp => {
      camp.ageGroupIds?.forEach(id => campAgeGroupIds.add(id))
      camp.periodIds?.forEach(id => campPeriodIds.add(id))

      if (camp.location_id)
        campLocationIds.add(camp.location_id)

      if (camp.location)
        campLocationNames.add(camp.location.trim().toLowerCase())
    })

    ageGroupsMenu = (ageGroupsResponse.data || []).map((ag: MenuItem) => ({
      label: ag.name,
      value: ag.id,
    })).filter(ag => campAgeGroupIds.has(ag.value))

    periodsMenu = (periodsResponse.data || []).map((p: MenuItem) => ({
      label: p.name,
      value: p.id,
    })).filter(p => campPeriodIds.has(p.value))

    categoriesMenu = (categoriesResponse.data || []).map((c: MenuItem) => ({
      label: c.name,
      value: c.id,
    }))

    locationsMenu = (locationsResponse.data || []).map((l: MenuItem) => ({
      label: l.city || l.name,
      value: l.id,
    })).filter(l => {
      const label = (l.label || '').trim().toLowerCase()

      return campLocationIds.has(l.value) || (label && campLocationNames.has(label))
    })
  }
  catch (error) {
    logApiError('/navigation/menu-data', error)
  }

  // Build navigation structure
  const navigation: NavItem[] = [
    {
      title: 'Welkom',
      to: { name: 'index' },
      icon: { icon: 'bx-home' },
    },

    // tKampFabrik with dynamic submenus
    {
      title: 'KampFabrik',
      to: { name: 'kampen' },
      icon: { icon: 'bx-grid-alt' },
      children: [
        {
          title: 'Alle kampen',
          to: { name: 'kampen' },
        },
        ...(ageGroupsMenu.length > 0
          ? [
            {
              title: 'Leeftijd',
              icon: { icon: 'bx-user' },
              children: [
                ...ageGroupsMenu.map(ag => ({
                  title: ag.label,
                  to: { name: 'kampen', query: { age: ag.value } },
                  icon: { icon: 'bx-user' },
                })),
              ],
            },
          ]
          : []),
        ...(periodsMenu.length > 0
          ? [
            {
              title: 'Periode',
              icon: { icon: 'bx-calendar' },
              children: [
                ...periodsMenu.map(p => ({
                  title: p.label,
                  to: { name: 'kampen', query: { periode: p.value } },
                  icon: { icon: 'bx-calendar' },
                })),
              ],
            },
          ]
          : []),
        ...(locationsMenu.length > 0
          ? [
            {
              title: 'Locatie',
              icon: { icon: 'bx-map' },
              children: [
                ...locationsMenu.map(l => ({
                  title: l.label,
                  to: { name: 'kampen', query: { location: l.value } },
                  icon: { icon: 'bx-map' },
                })),
              ],
            },
          ]
          : []),

        // Only show Thema if there are categories available
        ...(categoriesMenu.length > 0
          ? [
            {
              title: 'Thema',
              icon: { icon: 'bx-category' },
              children: [
                ...categoriesMenu.map(c => ({
                  title: c.label,
                  to: { name: 'kampen', query: { category: c.value } },
                  icon: { icon: 'bx-category' },
                })),
              ],
            },
          ]
          : []),
      ],
    },

    // ZwemFabrik
    {
      title: 'ZwemFabrik',
      to: { name: 'zwemmen' },
      icon: { icon: 'bx-swim' },
    },

    // FeestFabrik
    {
      title: 'FeestFabrik',
      to: { name: 'feesten' },
      icon: { icon: 'bx-party' },
    },

    // Ontdek 't SpeelFabrik
    {
      title: 'Ontdek \'t SpeelFabrik',
      icon: { icon: 'bx-info-circle' },
      children: [
        {
          title: 'Over \'t SpeelFabrik',
          to: { name: 'over-speelfabrik' },
          icon: { icon: 'bx-info-circle' },
        },
        {
          title: 'Ons Team',
          to: { name: 'ons-team' },
          icon: { icon: 'bx-group' },
        },
        {
          title: 'Partners',
          to: { name: 'partners' },
          icon: { icon: 'bx-heart' },
        },
        {
          title: 'Word Monitor',
          to: { name: 'word-monitor' },
          icon: { icon: 'bx-user-plus' },
        },
        {
          title: 'Nieuws',
          to: { name: 'nieuws' },
          icon: { icon: 'bx-news  ' },
        },
        {
          title: 'Praktische Info',
          icon: { icon: 'bx-help-circle' },
          children: [
            {
              title: 'FAQ',
              to: { name: 'faq' },
              icon: { icon: 'bx-help-circle' },
            },
            {
              title: 'Kortingen',
              to: { name: 'kortingen' },
              icon: { icon: 'bx-purchase-tag' },
            },
            {
              title: 'Fiscale attesten',
              to: { name: 'fiscale-attesten' },
              icon: { icon: 'bx-file' },
            },
          ],
        },
      ],
    },

    // Contact
    {
      title: 'Contact',
      to: { name: 'contact' },
      icon: { icon: 'bx-envelope' },
    },
  ]

  return navigation
}

export type { NavItem }
